import { MORALIS_CHAINS } from "@/types/enums/WalletTypeEnum";
import { BASE_URL, BIRD_EYE_API_KEY, NEXTJS_SERVER_BASE_URL } from "@/utils";
import getToken from "@/utils/getToken";
import getAxiosObj from "@/utils/request";
import axios, { AxiosRequestConfig } from "axios";

const BIRDEYE_WALLET_PORTFOLIO_URL =
  "https://public-api.birdeye.so/v1/wallet/token_list";

interface FetchWalletPortfolioProps {
  walletAddress: string;
  chain?: string;
}
interface PortfolioApiResponse {
  wallet: string;
  totalUsd: number;
  items: IPortfolioAssets[];
}
export interface IPortfolioAssets {
  address: string;
  decimals: number;
  balance: number;
  uiAmount: number;
  chainId: string;
  name: string;
  symbol: string;
  icon: string;
  logo_uri: string;
  priceUsd: number;
  valueUsd: number;
  marketCap?: number;
  priceChange24h?: number;
  valueChangeInUsd?: number;
  value: number;
  valueKey?: string;
}

export const getWalletPortfolio = async ({
  walletAddress,
  chain = "solana",
}: FetchWalletPortfolioProps): Promise<PortfolioApiResponse> => {
  try {
    //Fetch metadata in chunks
    if (chain === "solana") {
      // ✅ Fetch data from BirdEye API for Solana
      const response = await axios.get(BIRDEYE_WALLET_PORTFOLIO_URL, {
        params: { wallet: walletAddress },
        headers: {
          "X-API-KEY": BIRD_EYE_API_KEY,
          accept: "application/json",
          "x-chain": chain,
        },
      });

      return response?.data?.data;
    } else {
      // ✅ Fetch data from Moralis API for other chains
      const moralisChain = MORALIS_CHAINS[chain as keyof typeof MORALIS_CHAINS];

      if (!moralisChain || moralisChain === "none") {
        return {} as PortfolioApiResponse;
      }

      let url = `${BASE_URL}/bro/GetWalletTokensList?address=${walletAddress}&chain=${moralisChain}`;

      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        maxBodyLength: Infinity,
        method: "GET",
        url,
      };
      const response = await axios.request(config);
      // Convert Moralis API response to PortfolioApiResponse format
      const moralisData = Array.isArray(response?.data) ? response.data : [];

      const formattedData: PortfolioApiResponse = {
        wallet: walletAddress,
        totalUsd: 0,
        items: moralisData.map((item: any) => ({
          address: item.token_address,
          decimals: item.decimals,
          balance: parseFloat(item.balance),
          uiAmount: parseFloat(item.balance) / Math.pow(10, item.decimals),
          chainId: chain,
          name: item.name,
          symbol: item.symbol,
          icon: item.logo || item.thumbnail || "/default-logo.svg",
          logo_uri: item.logo || item.thumbnail || "/default-logo.svg",
          priceUsd: 0, // Replace with actual price if needed
          valueUsd: 0, // Replace with actual value if needed
          value: parseFloat(item.balance) / Math.pow(10, item.decimals),
        })),
      };

      return formattedData;
    }
  } catch (error) {
    console.error("Error fetching token metadata:", error);
    throw error;
  }
};
